import { useState, useMemo, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Pencil, Globe, Check, Loader2, Plus, Search, X, AlertCircle } from 'lucide-react';
import { ALL_LANGUAGES, FAMOUS_LANGUAGES } from '@/lib/constants/languages';
import { cn } from '@/lib/utils';

interface LanguagesCardProps {
  languages: string[];
  isEditing: boolean;
  isSaving: boolean;
  onEditClick: () => void;
  onCancel: () => void;
  onSave: () => void;
  onLanguageToggle: (language: string) => void;
  onLanguagesChange?: (languages: string[]) => void;
}

export function LanguagesCard({
  languages,
  isEditing,
  isSaving,
  onEditClick,
  onCancel,
  onSave,
  onLanguageToggle,
  onLanguagesChange,
}: LanguagesCardProps) {
  const isEmpty = languages.length === 0 && !isEditing;
  const [searchQuery, setSearchQuery] = useState('');
  const [showAllLanguages, setShowAllLanguages] = useState(false);
  const [error, setError] = useState<string>('');
  const previousLanguagesRef = useRef<string[]>([]);
  const prevIsEditingRef = useRef<boolean>(false);
  
  // Track previous languages when editing starts
  useEffect(() => {
    // Only capture the previous languages when transitioning from non-editing to editing
    if (isEditing && !prevIsEditingRef.current) {
      previousLanguagesRef.current = [...languages];
      setError('');
    }
    prevIsEditingRef.current = isEditing;
  }, [isEditing, languages]);
  
  // Reset search state when exiting edit mode
  useEffect(() => {
    if (!isEditing) {
      setSearchQuery('');
      setShowAllLanguages(false);
      setError('');
    }
  }, [isEditing]);
  
  // Filter languages based on search query and whether to show all or just famous ones
  const filteredLanguages = useMemo(() => {
    const languagesToShow = showAllLanguages || searchQuery ? ALL_LANGUAGES : FAMOUS_LANGUAGES;
    
    if (!searchQuery) {
      return Array.from(languagesToShow);
    }
    
    const query = searchQuery.toLowerCase();
    return ALL_LANGUAGES.filter(lang => 
      lang.toLowerCase().includes(query)
    );
  }, [searchQuery, showAllLanguages]);
  
  return (
    <div className={cn(
      "rounded-xl sm:rounded-[20px] p-4 sm:p-6 shadow-sm",
      isEmpty
        ? "bg-amber-50/50 border-2 border-dashed border-amber-500/30"
        : "bg-white border border-slate-200"
    )}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Globe className="h-5 w-5 text-slate-700" />
          <h3 className="text-lg font-semibold text-slate-900">Languages</h3>
          {isEmpty && (
            <Badge className="bg-amber-500 text-white rounded-[48px] px-3 sm:px-4 py-1 text-xs">
              Incomplete
            </Badge>
          )}
        </div>
        {!isEditing && !isEmpty && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onEditClick}
            className="text-[#4669ed]"
          >
            <Pencil className="h-4 w-4" />
          </Button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-4">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              type="text"
              placeholder={showAllLanguages || searchQuery ? "Search all languages..." : "Search popular languages..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-9 rounded-full"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          
          {/* Toggle between famous and all languages */}
          {!searchQuery && (
            <div className="flex items-center gap-2">
            <Button
              variant={showAllLanguages ? "default" : "outline"}
              size="sm"
              onClick={() => setShowAllLanguages(true)}
              className={cn(
                "rounded-full text-xs",
                showAllLanguages ? "bg-[#4669ed] hover:bg-[#2b42a3] text-white" : ""
              )}
            >
              All ({ALL_LANGUAGES.length})
            </Button>
              <Button
                variant={!showAllLanguages ? "default" : "outline"}
                size="sm"
                onClick={() => setShowAllLanguages(false)}
                className={cn(
                  "rounded-full text-xs",
                  !showAllLanguages ? "bg-[#4669ed] hover:bg-[#2b42a3] text-white" : ""
                )}
              >
                Popular ({FAMOUS_LANGUAGES.length})
              </Button>
            </div>
          )}
          
          {/* Language Selection Area */}
          <ScrollArea className="h-[300px] w-full rounded-lg border border-slate-200 p-3">
            <div className="flex flex-wrap gap-2">
              {filteredLanguages.map((language) => (
                <Badge
                  key={language}
                  variant={languages.includes(language) ? 'default' : 'outline'}
                  className={cn(
                    "cursor-pointer transition-colors rounded-full px-3 py-1.5 text-sm",
                    languages.includes(language)
                      ? 'bg-[#d1e0fc] text-[#2b42a3] hover:bg-[#c1d4fb] border-transparent'
                      : 'hover:bg-slate-100 border-slate-200'
                  )}
                  onClick={() => {
                    const isCurrentlySelected = languages.includes(language);
                    onLanguageToggle(language);
                    // Clear error when user adds a language (not when removing)
                    if (error && !isCurrentlySelected) {
                      setError('');
                    }
                  }}
                >
                  {languages.includes(language) && <Check className="h-3 w-3 mr-1" />}
                  {language}
                </Badge>
              ))}
            </div>
            {filteredLanguages.length === 0 && (
              <div className="text-center py-8 text-slate-500 text-sm">
                No languages found matching "{searchQuery}"
              </div>
            )}
          </ScrollArea>
          
          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 text-red-600 text-sm p-3 bg-red-50 rounded-lg border border-red-200">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => {
                // Restore previous languages
                const previousLanguages = previousLanguagesRef.current;
                
                // If onLanguagesChange is provided, use it for direct restoration (preferred method)
                if (onLanguagesChange) {
                  onLanguagesChange(previousLanguages);
                  setSearchQuery('');
                  setShowAllLanguages(false);
                  setError('');
                  onCancel();
                } else {
                  // Fallback: restore by toggling languages
                  // This approach ensures we restore to the exact previous state
                  const currentSet = new Set(languages);
                  const previousSet = new Set(previousLanguages);
                  
                  // First, remove all languages that shouldn't be there
                  const toRemove = languages.filter(lang => !previousSet.has(lang));
                  // Then, add all languages that should be there
                  const toAdd = previousLanguages.filter(lang => !currentSet.has(lang));
                  
                  // Apply removals first, then additions
                  // Use setTimeout to ensure React processes each update
                  toRemove.forEach((lang, index) => {
                    setTimeout(() => {
                      if (!previousSet.has(lang)) {
                        onLanguageToggle(lang);
                      }
                    }, index * 10);
                  });
                  
                  // Add languages after removals complete
                  toAdd.forEach((lang, index) => {
                    setTimeout(() => {
                      if (previousSet.has(lang)) {
                        onLanguageToggle(lang);
                      }
                    }, (toRemove.length * 10) + (index * 10));
                  });
                  
                  setSearchQuery('');
                  setShowAllLanguages(false);
                  setError('');
                  
                  // Small delay to ensure state restoration completes before closing edit mode
                  const totalDelay = Math.max(toRemove.length, toAdd.length) * 10 + 50;
                  setTimeout(() => {
                    onCancel();
                  }, totalDelay);
                }
              }} 
              disabled={isSaving}
              className="flex-1 rounded-full"
            >
              Cancel
            </Button>
            <Button 
              size="sm" 
              onClick={() => {
                // Validate that at least one language is selected
                if (languages.length === 0) {
                  setError('Please select at least one language');
                  return;
                }
                
                setSearchQuery('');
                setShowAllLanguages(false);
                setError('');
                onSave();
              }} 
              disabled={isSaving || languages.length === 0}
              className="flex-1 bg-[#4669ed] hover:bg-[#2b42a3] rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {languages.length > 0 ? languages.map((language) => (
            <Badge
              key={language}
              className="bg-[#d1e0fc] text-[#2b42a3] rounded-full px-3 py-1.5 text-xs font-semibold"
            >
              {language}
            </Badge>
          )) : (
            <div className="flex flex-col items-center py-4 w-full">
              {/* Icon Circle */}
              <div className="w-20 h-20 rounded-[40px] bg-slate-200 flex items-center justify-center mb-6">
                <Globe className="w-6 h-6 text-slate-500" />
              </div>

              {/* Title */}
              <h3 className="text-xl font-normal text-slate-900 text-center mb-2">
                Select Your Languages
              </h3>

              {/* Description */}
              <p className="text-sm text-slate-500 text-center max-w-[280px] leading-relaxed mb-6">
                Let clients know which languages you can communicate in.
              </p>

              {/* CTA Button */}
              <Button
                onClick={onEditClick}
                className="bg-[#4669ed] hover:bg-[#2b42a3] rounded-[48px] px-6"
              >
                <Plus className="w-5 h-5" />
                Add Languages
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
